import makeDicomImageFromDetailsAndViewport, {
  DicomExportableImage,
} from 'app/dicom/makeDicomImageFromDetailsAndViewport';
import InvalidImageError from 'app/errors/InvalidImageError';
import NoPACSCommunicationError from 'app/errors/NoPACSCommunicationError';
import NotFoundError from 'app/errors/NotFoundError';
import IDicomBuilder from 'app/interfaces/IDicomBuilder';
import { PACSCommunication, PACSError, RequestSuccess } from 'app/interfaces/PACSCommunication';
import { Patient } from 'app/interfaces/Patient';
import { Err, Ok, Result } from 'app/type-utils/Result';
import isCompatibleWithPACS from 'app/utils/image/isCompatibleWithPACS';
import _ from 'lodash';
import { IntlShape } from 'react-intl';

type ImageInfo = {
  studyId: string;
  image: DicomExportableImage & { origin: string };
  patient: Patient;
  imagesOrder: string[];
};

type SyncOptions = {
  includeAnnotations: boolean;
  signal?: AbortSignal;
};

type SyncImageWithPACSDependencies = {
  intl: IntlShape;
  dicomBuilder: IDicomBuilder;
  pacsCommunication?: PACSCommunication;
  clinicInfo?: Record<string, any> | null;
};

export type PACSStoreError =
  | PACSError
  | NoPACSCommunicationError
  | InvalidImageError
  | NotFoundError;

export default async function syncImageWithPACS(
  imageInfo: ImageInfo,
  dependencies: SyncImageWithPACSDependencies,
  options?: SyncOptions
): Promise<Result<RequestSuccess, PACSStoreError>> {
  const { patient, imagesOrder, studyId, image } = imageInfo;
  const { intl, dicomBuilder, pacsCommunication } = dependencies;
  const { signal, includeAnnotations = false } = options ?? {};

  if (!pacsCommunication) {
    return Err(new NoPACSCommunicationError());
  }
  if (!isCompatibleWithPACS(image)) {
    return Err(new InvalidImageError('Image not compatible with PACS'));
  }

  // For PACS synchronization, by default we only save Lateralization markers
  // When includeAnnotations is true, we include all annotations
  const savedAnnotations = includeAnnotations
    ? image.annotations
    : _.pick(image.annotations, ['Crop', 'LeftMarker', 'RightMarker']);

  const pacsStoredDicomData = await makeDicomImageFromDetailsAndViewport(
    {
      studyId,
      image: { ...image, annotations: savedAnnotations },
      patient,
      imagesOrder,
      forceUniqueID: true,
      transferSyntax: undefined,
    },
    { intl, dicomBuilder, clinicInfo: dependencies.clinicInfo }
  );

  try {
    return Ok<RequestSuccess>(await pacsCommunication.store(pacsStoredDicomData, { signal }));
  } catch (error: any) {
    if (error.name === 'AbortError') {
      return Err<PACSError>({
        error_type: 'send_error',
        status: 'BROKEN_ASSOCIATION',
      } as PACSError);
    }
    if ('error_type' in (error ?? {})) {
      return Err<PACSError>(error);
    }
    return Err<Error>(error);
  }
}
